import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '../../../../components/Buttons/IconButton';
import { IoCreateOutline, IoTrashOutline } from 'react-icons/io5';
import { BsTrash3, BsDownload } from 'react-icons/bs';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import { del, get, getBlob } from '../../../../utils/helpers/api';
import { toast } from 'react-toastify';
import DefaultButton from '../../../../components/Buttons/Default';
import DefaultPagination from '../../../../components/Pagination';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import { TfiFilter } from 'react-icons/tfi';
import FiltersDrawer from './Drawers/Filters';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/crm/calls/edit/${data.id}`, { state: { callData: data } });
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCallToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function CallsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [callToDelete, setCallToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const FILTER_KEYS = ['subject', 'type', 'min_duration', 'max_duration', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(v => v).length;

  const getCalls = useCallback(async () => {
    setLoading(true);
    let query = `/crm/calls?page=${currentPage}&page_size=${pageSize}`;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Object.entries(activeFilters).forEach(([key, value]) => {
      query += `&${key}=${encodeURIComponent(value)}`;
    });
    const data = await get(query);
    if (data?.calls?.data) {
      setRowData(data.calls.data);
      setTotalPages(data.calls.last_page);
      setTotalRecords(data.calls.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getCalls();
  }, [getCalls]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  }

  const exportCalls = async () => {
    const data = await getBlob('/crm/calls/export')
      .then(blob => {
        if (!blob) throw new Error('export failed');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `calls-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(err => {
        toast.error(t('common.somethingWentWrong'));
      });
  }

  const deleteCall = async () => {
    const data = await del('/crm/calls/delete/' + callToDelete);
    if (data?.success) {
      toast.success(data.message || t('crm.callDeleted'));
      getCalls();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setCallToDelete(null);
  };

  const columnDefs = useMemo(() => [
    { field: 'subject', headerName: t('common.subject'), minWidth: 200, flex: 1, sortable: true, onCellClicked: (params) => navigate(`/crm/calls/view/${params.data.id}`) },
    { field: 'call_type', headerName: t('crm.callType'), minWidth: 120, flex: 1, sortable: true },
    { field: 'call_duration', headerName: t('crm.callDuration'), minWidth: 120, flex: 1, sortable: true },
    {
      headerName: t('crm.customer'), minWidth: 150, flex: 1, sortable: true,
      valueGetter: (params) => {
        const firstname = params.data.customer?.firstname || '';
        const lastname = params.data.customer?.lastname || '';
        return `${firstname} ${lastname}`.trim() || t('common.notAvailable');
      }
    },
    { field: 'employee.firstname', headerName: t('crm.responsible'), minWidth: 160, flex: 1, sortable: true },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer },
  ], [navigate, t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setCallToDelete }), [navigate]);

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.calls')} onReload={getCalls} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton color="primary" size="sm" onClick={() => navigate('/crm/calls/create')}>
            {t('crm.newCall')}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={exportCalls}>
                <BsDownload size={15} /> {t('common.export')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ConfirmationModal
        isOpen={callToDelete !== null}
        title={t('crm.deleteCall')}
        message={t('crm.deleteCallConfirm')}
        onConfirm={deleteCall}
        toggle={() => setCallToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default CallsPage;
